import {
  ComplianceProgramParticipantStatus,
  GetComplianceProgramProgressQuery,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import Button, { buttonTheme } from "@components/button/button";
import Layout from "@components/layout/layout";
import ManagedTable from "@components/table/managed-table";
import { TableSticky } from "@components/table/table";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon";
import { getUrlUsingTypename } from "@helpers/helpers";

import {
  complianceProgramReportingTableColNameClassName,
  complianceProgramReportingTableColsClassName,
} from "./compliance-program-progress";
import {
  ComplianceProgramReportingEntityTableCol,
  ComplianceProgramReportingEntityTableParticipantStatusWithNominations,
} from "./compliance-program-progress-entity-table";
import ProgramProgressPill from "./program-progress-pill";

const ComplianceProgramProgressUserTable = ({
  participantStatuses,
  cols,
  title,
  complianceProgram,
  usersLabel,
}: {
  participantStatuses: ComplianceProgramReportingEntityTableParticipantStatusWithNominations[];
  cols: ComplianceProgramReportingEntityTableCol[];
  title: string;
  usersLabel: string;
  complianceProgram: NonNullable<
    GetComplianceProgramProgressQuery["complianceProgram"]
  >;
}) => {
  const label = useLabel();

  return (
    <Layout.MainSubSection title={title}>
      {participantStatuses.length === 0 ? (
        <div className="text-gray-500 text-sm">
          No {usersLabel} participating to this {label("program")}.
        </div>
      ) : (
        <ManagedTable
          scroll
          sortingEnabled
          sortDir="asc"
          sortKey="name"
          data={participantStatuses}
          className="overscroll-x-contain max-h-[80dvh]"
          aria-label="Compliance program progress user table"
          sticky={TableSticky.onlyTop}
          columns={[
            {
              headerClassName: complianceProgramReportingTableColNameClassName,
              cellClassName: complianceProgramReportingTableColNameClassName,
              header: `Name`,
              render: (
                data: ComplianceProgramReportingEntityTableParticipantStatusWithNominations
              ) => {
                const isEngagementSurvey =
                  !!complianceProgram.engagementSurveyTemplate;
                const assessmentDelivery = !isEngagementSurvey
                  ? complianceProgram.assessmentDeliveries.edges.find(
                      (edge) => edge?.node?.target?.id === data.user.id
                    )
                  : complianceProgram.assessmentDeliveries.edges[0];
                return (
                  <div className="min-w-0 flex flex-nowrap grow shrink-0 items-center justify-between gap-2">
                    <div
                      title={data.user.name}
                      className="text-ellipsis overflow-hidden text-nowrap"
                    >
                      {data.user.name}
                    </div>
                    <div className="w-[32px] shrink-0">
                      <Button
                        theme={buttonTheme.iconGray}
                        icon={TopicflowIconMap.externalLink}
                        disabled={!assessmentDelivery}
                        to={
                          assessmentDelivery?.node
                            ? isEngagementSurvey
                              ? `/survey/delivery/${assessmentDelivery.node.id}`
                              : getUrlUsingTypename(assessmentDelivery.node)
                            : undefined
                        }
                        tooltip={
                          assessmentDelivery
                            ? isEngagementSurvey
                              ? "Go to Survey Delivery"
                              : `Go to ${label("review", {
                                  capitalize: true,
                                })} Package`
                            : isEngagementSurvey
                            ? "No Survey Delivery Available"
                            : `No ${label("review", {
                                capitalize: true,
                              })} Package Available`
                        }
                      />
                    </div>
                  </div>
                );
              },
              sortKey: "name",
              value: (
                data: ComplianceProgramReportingEntityTableParticipantStatusWithNominations
              ) => {
                return data.user.name;
              },
            },
            ...cols.map((col) => ({
              headerClassName: complianceProgramReportingTableColsClassName,
              cellClassName: complianceProgramReportingTableColsClassName,
              header: col.label,
              sortKey: col.keyName,
              value: (
                data: ComplianceProgramReportingEntityTableParticipantStatusWithNominations
              ) =>
                data[col.keyName] ===
                ComplianceProgramParticipantStatus.Complete
                  ? 4
                  : data[col.keyName] ===
                    ComplianceProgramParticipantStatus.InProgress
                  ? 3
                  : data[col.keyName] ===
                    ComplianceProgramParticipantStatus.NotStarted
                  ? 2
                  : 1,
              render: (
                data: ComplianceProgramReportingEntityTableParticipantStatusWithNominations
              ) => {
                return (
                  <ProgramProgressPill
                    col={col}
                    participantStatus={data}
                    status={data[col.keyName]}
                    complianceProgram={complianceProgram}
                    className="hover:brightness-95"
                  />
                );
              },
            })),
            {
              header: ``,
              render: () => "",
            },
          ]}
        />
      )}
    </Layout.MainSubSection>
  );
};

export default ComplianceProgramProgressUserTable;
